// Real files from VERA's content: Word and PDF for documents, Excel for tables, PowerPoint for decks.
// Everything is generated here, on the person's computer, so nothing leaves it to make a file.

import { AlignmentType, BorderStyle, Document, Footer, HeadingLevel, LevelFormat, Packer, PageNumber, Paragraph, ShadingType, Table, TableCell, TableRow, TextRun, WidthType } from "docx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import PptxGenJS from "pptxgenjs";
import ExcelJS from "exceljs";
import type { Artifact } from "./artifacts";

export type Cell = string | number | null;
export type DocSpec =
  | { kind: "document"; title: string; subtitle?: string; markdown: string }
  | { kind: "spreadsheet"; title: string; sheets: { name: string; columns: string[]; rows: Cell[][] }[] }
  | { kind: "presentation"; title: string; subtitle?: string; slides: { title: string; bullets: string[]; notes?: string }[] };
export type FileFormat = "docx" | "pdf" | "xlsx" | "pptx";

export const MIME: Record<FileFormat, string> = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};
export const FORMAT_LABEL: Record<FileFormat, string> = { docx: "Word", pdf: "PDF", xlsx: "Excel", pptx: "PowerPoint" };

/** Which files each kind of content can become, the main one first. */
export function formatsFor(spec: DocSpec): FileFormat[] {
  return spec.kind === "document" ? ["docx", "pdf"] : spec.kind === "spreadsheet" ? ["xlsx", "pdf"] : ["pptx", "pdf"];
}

export function fileName(title: string, format: FileFormat) {
  const base = title.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80) || "VERA document";
  return `${base}.${format}`;
}

const PURPLE = "8A55F7";
const INK = "1C1530";

// ---------- markdown, the way VERA writes documents ----------
export type Block =
  | { type: "heading"; level: number; text: string }
  | { type: "paragraph"; text: string }
  | { type: "bullets"; ordered: boolean; items: string[] }
  | { type: "table"; rows: string[][] }
  | { type: "rule" };

export function parseMarkdown(md: string): Block[] {
  const blocks: Block[] = [];
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  let para: string[] = [];
  const flush = () => { if (para.length) { blocks.push({ type: "paragraph", text: para.join(" ").trim() }); para = []; } };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd();
    const t = line.trim();
    if (!t) { flush(); continue; }
    const h = /^(#{1,6})\s+(.*)$/.exec(t);
    if (h) { flush(); blocks.push({ type: "heading", level: h[1].length, text: h[2].trim() }); continue; }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) { flush(); blocks.push({ type: "rule" }); continue; }
    if (t.startsWith("|")) {
      flush();
      const rows: string[][] = [];
      for (; i < lines.length && lines[i].trim().startsWith("|"); i++) {
        const cells = lines[i].trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
        if (!cells.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(cells);
      }
      i--;
      if (rows.length) blocks.push({ type: "table", rows });
      continue;
    }
    const b = /^([-*+]|\d+[.)])\s+(.*)$/.exec(t);
    if (b) {
      flush();
      const ordered = /\d/.test(b[1]);
      const last = blocks[blocks.length - 1];
      if (last?.type === "bullets" && last.ordered === ordered) last.items.push(b[2]);
      else blocks.push({ type: "bullets", ordered, items: [b[2]] });
      continue;
    }
    para.push(t);
  }
  flush();
  return blocks;
}

/** **bold** and *italic* runs; `code` loses its backticks. */
export function inlineRuns(text: string): { text: string; bold?: boolean; italics?: boolean }[] {
  const out: { text: string; bold?: boolean; italics?: boolean }[] = [];
  const re = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\s][^*]*\*|`[^`]+`)/g;
  let at = 0;
  for (const m of text.matchAll(re)) {
    if (m.index! > at) out.push({ text: text.slice(at, m.index) });
    const s = m[0];
    if (s.startsWith("**") || s.startsWith("__")) out.push({ text: s.slice(2, -2), bold: true });
    else if (s.startsWith("`")) out.push({ text: s.slice(1, -1) });
    else out.push({ text: s.slice(1, -1), italics: true });
    at = m.index! + s.length;
  }
  if (at < text.length) out.push({ text: text.slice(at) });
  return out.filter((r) => r.text);
}
const plain = (text: string) => inlineRuns(text).map((r) => r.text).join("");

// ---------- Word ----------
export async function toDocx(spec: DocSpec): Promise<Uint8Array> {
  const children: (Paragraph | Table)[] = [new Paragraph({ heading: HeadingLevel.TITLE, children: [new TextRun({ text: spec.title, color: INK })] })];
  if ("subtitle" in spec && spec.subtitle) children.push(new Paragraph({ children: [new TextRun({ text: spec.subtitle, italics: true, color: "6B6385" })], spacing: { after: 240 } }));
  const table = (rows: string[][]) => new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: rows.map((r, ri) => new TableRow({
      tableHeader: ri === 0,
      children: r.map((c) => new TableCell({
        shading: ri === 0 ? { type: ShadingType.CLEAR, color: "auto", fill: PURPLE } : undefined,
        children: [new Paragraph({ children: inlineRuns(c).map((x) => new TextRun({ ...x, ...(ri === 0 ? { bold: true, color: "FFFFFF" } : {}) })) })],
      })),
    })),
  });
  if (spec.kind === "document") {
    for (const b of parseMarkdown(spec.markdown)) {
      if (b.type === "heading") children.push(new Paragraph({ heading: [HeadingLevel.HEADING_1, HeadingLevel.HEADING_2, HeadingLevel.HEADING_3][Math.min(b.level, 3) - 1], children: [new TextRun({ text: plain(b.text) })] }));
      else if (b.type === "paragraph") children.push(new Paragraph({ children: inlineRuns(b.text).map((r) => new TextRun(r)), spacing: { after: 120 } }));
      else if (b.type === "bullets") for (const item of b.items) children.push(new Paragraph({ children: inlineRuns(item).map((r) => new TextRun(r)), ...(b.ordered ? { numbering: { reference: "numbers", level: 0 } } : { bullet: { level: 0 } }) }));
      else if (b.type === "table") { children.push(table(b.rows)); children.push(new Paragraph({ text: "" })); }
      else children.push(new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: "D9D3EA", space: 1 } }, text: "" }));
    }
  } else if (spec.kind === "spreadsheet") {
    for (const s of spec.sheets) {
      children.push(new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: s.name })] }));
      children.push(table([s.columns, ...s.rows.map((r) => r.map((c) => (c === null ? "" : String(c))))]));
    }
  } else {
    for (const s of spec.slides) {
      children.push(new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: s.title })] }));
      for (const b of s.bullets) children.push(new Paragraph({ bullet: { level: 0 }, children: inlineRuns(b).map((r) => new TextRun(r)) }));
    }
  }
  const doc = new Document({
    creator: "VERA", title: spec.title,
    styles: { default: { document: { run: { font: "Calibri", size: 22 } } } },
    numbering: { config: [{ reference: "numbers", levels: [{ level: 0, format: LevelFormat.DECIMAL, text: "%1.", alignment: AlignmentType.START }] }] },
    sections: [{
      footers: { default: new Footer({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ children: [PageNumber.CURRENT, " / ", PageNumber.TOTAL_PAGES], size: 18, color: "8A8499" })] })] }) },
      children,
    }],
  });
  return new Uint8Array(await Packer.toArrayBuffer(doc));
}

// ---------- PDF ----------
// The standard PDF fonts cover Western European text; anything outside it is swapped for a close equivalent.
const PDF_SAFE: Record<string, string> = { "→": "->", "←": "<-", "≤": "<=", "≥": ">=", "✓": "v", "✔": "v", "✗": "x", " ": " ", "−": "-", "‐": "-", "‑": "-" };
export function pdfSafe(s: string) {
  return s.replace(/[→←≤≥✓✔✗ −‐‑]/g, (c) => PDF_SAFE[c] ?? c)
    .replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}]/gu, "")
    .replace(/[^\u0000-ÿŒœŠšŸŽžƒˆ˜–—‘’‚“”„†‡•…‰‹›€™]/g, "?");
}

/** A deck as a widescreen PDF, one slide per page, so it opens straight in the browser (PowerPoint stays for editing). */
function slidesPdf(spec: Extract<DocSpec, { kind: "presentation" }>): Uint8Array {
  const pdf = new jsPDF({ unit: "pt", format: [960, 540], orientation: "landscape" });
  const W = 960, H = 540, M = 56;
  const slide = (title: string, draw: () => void, first = false) => {
    if (!first) pdf.addPage([960, 540], "landscape");
    pdf.setFillColor(248, 246, 253); pdf.rect(0, 0, W, H, "F");
    pdf.setFillColor(138, 85, 247); pdf.rect(0, 0, 10, H, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(28); pdf.setTextColor(28, 21, 48);
    pdf.text(pdf.splitTextToSize(pdfSafe(title), W - 2 * M) as string[], M, 90);
    draw();
  };
  slide(spec.title, () => {
    if (spec.subtitle) { pdf.setFont("helvetica", "normal"); pdf.setFontSize(16); pdf.setTextColor(107, 99, 133); pdf.text(pdf.splitTextToSize(pdfSafe(spec.subtitle), W - 2 * M) as string[], M, 140); }
  }, true);
  for (const s of spec.slides) slide(s.title, () => {
    let y = 150;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(17); pdf.setTextColor(40, 34, 60);
    for (const b of s.bullets) {
      const lines = pdf.splitTextToSize(pdfSafe(plain(b)), W - 2 * M - 26) as string[];
      if (y + lines.length * 24 > H - 50) break;
      pdf.setFillColor(138, 85, 247); pdf.circle(M + 5, y - 6, 3.5, "F");
      pdf.text(lines, M + 22, y); y += lines.length * 24 + 10;
    }
  });
  const pages = pdf.getNumberOfPages();
  for (let p = 2; p <= pages; p++) { pdf.setPage(p); pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(138, 132, 153); pdf.text(`${p} / ${pages}`, W - M, H - 24, { align: "right" }); }
  pdf.setProperties({ title: spec.title, creator: "VERA" });
  return new Uint8Array(pdf.output("arraybuffer"));
}

export function toPdf(spec: DocSpec): Uint8Array {
  if (spec.kind === "presentation") return slidesPdf(spec);
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth(), H = pdf.internal.pageSize.getHeight(), M = 56, maxW = W - 2 * M;
  let y = M;
  const room = (h: number) => { if (y + h > H - M) { pdf.addPage(); y = M; } };
  const text = (s: string, size: number, opts: { bold?: boolean; italic?: boolean; color?: [number, number, number]; indent?: number; gap?: number } = {}) => {
    pdf.setFont("helvetica", opts.bold ? (opts.italic ? "bolditalic" : "bold") : opts.italic ? "italic" : "normal");
    pdf.setFontSize(size);
    pdf.setTextColor(...(opts.color ?? [28, 21, 48]));
    const lines = pdf.splitTextToSize(pdfSafe(s), maxW - (opts.indent ?? 0)) as string[];
    const lh = size * 1.35;
    for (const l of lines) { room(lh); pdf.text(l, M + (opts.indent ?? 0), y + size); y += lh; }
    y += opts.gap ?? size * 0.5;
  };
  const table = (rows: string[][]) => {
    autoTable(pdf, {
      startY: y, margin: { left: M, right: M }, head: [rows[0].map(pdfSafe)], body: rows.slice(1).map((r) => r.map((c) => pdfSafe(plain(c)))),
      styles: { font: "helvetica", fontSize: 9, cellPadding: 4, textColor: [28, 21, 48] }, headStyles: { fillColor: [138, 85, 247], textColor: 255 },
      alternateRowStyles: { fillColor: [246, 243, 252] },
    });
    y = ((pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 14;
  };
  text(spec.title, 20, { bold: true, gap: 6 });
  if ("subtitle" in spec && spec.subtitle) text(spec.subtitle, 11, { italic: true, color: [107, 99, 133], gap: 12 });
  if (spec.kind === "document") {
    for (const b of parseMarkdown(spec.markdown)) {
      if (b.type === "heading") { y += 6; text(plain(b.text), b.level <= 1 ? 15 : b.level === 2 ? 13 : 11.5, { bold: true, gap: 4 }); }
      else if (b.type === "paragraph") text(plain(b.text), 10.5);
      else if (b.type === "bullets") { b.items.forEach((it, n) => text(`${b.ordered ? `${n + 1}.` : "•"}  ${plain(it)}`, 10.5, { indent: 12, gap: 2 })); y += 4; }
      else if (b.type === "table") table(b.rows);
      else { room(10); pdf.setDrawColor(217, 211, 234); pdf.line(M, y + 4, W - M, y + 4); y += 12; }
    }
  } else if (spec.kind === "spreadsheet") {
    for (const s of spec.sheets) { text(s.name, 13, { bold: true, gap: 4 }); table([s.columns, ...s.rows.map((r) => r.map((c) => (c === null ? "" : String(c))))]); }
  }
  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    pdf.setPage(p); pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(138, 132, 153);
    pdf.text(`${p} / ${pages}`, W / 2, H - 28, { align: "center" });
  }
  pdf.setProperties({ title: spec.title, creator: "VERA" });
  return new Uint8Array(pdf.output("arraybuffer"));
}

// ---------- Excel ----------
export async function toXlsx(spec: DocSpec): Promise<Uint8Array> {
  const wb = new ExcelJS.Workbook();
  wb.creator = "VERA";
  const sheets = spec.kind === "spreadsheet" ? spec.sheets
    : spec.kind === "document" ? parseMarkdown(spec.markdown).filter((b): b is Extract<Block, { type: "table" }> => b.type === "table").map((t, i) => ({ name: `Table ${i + 1}`, columns: t.rows[0], rows: t.rows.slice(1) as Cell[][] }))
    : [{ name: "Slides", columns: ["Slide", "Points"], rows: spec.slides.map((s) => [s.title, s.bullets.join("\n")]) }];
  if (!sheets.length) throw new Error("There's no table in this to put into Excel.");
  const used = new Set<string>();
  for (const s of sheets) {
    let name = (s.name || "Sheet").replace(/[\\/*?:[\]]/g, " ").slice(0, 31).trim() || "Sheet";
    for (let n = 2; used.has(name.toLowerCase()); n++) name = `${name.slice(0, 28)} ${n}`;
    used.add(name.toLowerCase());
    const ws = wb.addWorksheet(name, { views: [{ state: "frozen", ySplit: 1 }] });
    ws.addRow(s.columns);
    for (const r of s.rows) ws.addRow(r.map((c) => (typeof c === "string" && /^-?\d+(\.\d+)?$/.test(c.trim()) ? Number(c) : c)));
    const head = ws.getRow(1);
    head.font = { bold: true, color: { argb: "FFFFFFFF" } };
    head.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${PURPLE}` } };
    head.alignment = { vertical: "middle" };
    ws.columns.forEach((col, i) => {
      const width = Math.max(String(s.columns[i] ?? "").length, ...s.rows.map((r) => String(r[i] ?? "").split("\n")[0].length));
      col.width = Math.min(60, Math.max(10, width + 2));
    });
    if (s.columns.length) ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: s.columns.length } };
  }
  return new Uint8Array(await wb.xlsx.writeBuffer());
}

// ---------- PowerPoint ----------
export async function toPptx(spec: DocSpec): Promise<Uint8Array> {
  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE";
  pptx.title = spec.title;
  pptx.author = "VERA";
  const slides = spec.kind === "presentation" ? spec.slides
    : spec.kind === "document" ? parseMarkdown(spec.markdown).reduce<{ title: string; bullets: string[] }[]>((acc, b) => {
      if (b.type === "heading") acc.push({ title: plain(b.text), bullets: [] });
      else { const cur = acc[acc.length - 1] ?? (acc.push({ title: spec.title, bullets: [] }), acc[acc.length - 1]); if (b.type === "bullets") cur.bullets.push(...b.items.map(plain)); else if (b.type === "paragraph") cur.bullets.push(plain(b.text)); }
      return acc;
    }, [])
    : spec.sheets.map((s) => ({ title: s.name, bullets: s.rows.slice(0, 8).map((r) => r.filter((c) => c !== null).join("  |  ")) }));
  const cover = pptx.addSlide();
  cover.background = { color: "140F2E" };
  cover.addText(spec.title, { x: 0.8, y: 2.4, w: 11.7, h: 1.4, fontSize: 40, bold: true, color: "FFFFFF", fontFace: "Calibri" });
  if ("subtitle" in spec && spec.subtitle) cover.addText(spec.subtitle, { x: 0.8, y: 3.8, w: 11.7, h: 0.8, fontSize: 20, color: "C9B8FF", fontFace: "Calibri" });
  for (const s of slides) {
    const slide = pptx.addSlide();
    slide.addShape("rect", { x: 0, y: 0, w: 13.33, h: 0.12, fill: { color: PURPLE }, line: { color: PURPLE } });
    slide.addText(s.title, { x: 0.6, y: 0.4, w: 12.1, h: 0.9, fontSize: 28, bold: true, color: INK, fontFace: "Calibri" });
    if (s.bullets.length) {
      slide.addText(s.bullets.map((b) => ({ text: plain(b), options: { bullet: true, breakLine: true } })),
        { x: 0.8, y: 1.5, w: 11.7, h: 5.4, fontSize: s.bullets.length > 7 ? 16 : 20, color: "2E2843", fontFace: "Calibri", valign: "top", paraSpaceAfter: 8 });
    }
    const notes = (s as { notes?: string }).notes;
    if (notes) slide.addNotes(notes);
  }
  const out = await pptx.write({ outputType: "uint8array" });
  return out as Uint8Array;
}

export async function generate(spec: DocSpec, format: FileFormat): Promise<Uint8Array> {
  if (format === "docx") return toDocx(spec);
  if (format === "pdf") return toPdf(spec);
  if (format === "xlsx") return toXlsx(spec);
  return toPptx(spec);
}

// ---------- the Create cards (RACI, timetable, matrix, documents) ----------
export function artifactToSpec(a: Artifact): DocSpec {
  if (a.kind === "raci") return { kind: "spreadsheet", title: a.title, sheets: [{ name: "RACI", columns: ["Task", ...a.people], rows: a.rows.map((r) => [r.task, ...r.cells]) }] };
  if (a.kind === "timetable") return { kind: "spreadsheet", title: a.title, sheets: [{ name: "Timetable", columns: ["Task", "Owner", "Start week", "End week", "Milestone"], rows: a.rows.map((r) => [r.task, r.owner, r.start, r.end, r.milestone ? "Yes" : ""]) }] };
  if (a.kind === "matrix") return { kind: "spreadsheet", title: a.title, sheets: [{ name: "Priorities", columns: ["Quadrant", "Item"], rows: a.quadrants.flatMap((q) => q.items.map((it) => [q.name, it])) }] };
  return { kind: "document", title: a.title, markdown: a.body };
}
